import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import { certificatesApi, CertificateType } from '@/api/services/certificates.api';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

const certTypeLabels: Record<CertificateType, string> = {
  MILL_TEST_CERTIFICATE: 'Mill Test Certificate',
  CERTIFICATE_OF_CONFORMANCE: 'Certificate of Conformance',
  API_Q1: 'API Q1',
  API_Q2: 'API Q2',
  NACE_MR0175: 'NACE MR0175',
  ISO_CERTIFICATION: 'ISO Certification',
  MTR: 'Mill Test Report',
  COC: 'Certificate of Conformance',
  COA: 'Certificate of Analysis',
  CALIBRATION: 'Calibration',
  SAFETY: 'Safety',
  ENVIRONMENTAL: 'Environmental',
  QUALITY: 'Quality',
  OTHER: 'Other',
};

export default function ComplianceNew() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    type: 'MTR' as CertificateType,
    name: '',
    issuedBy: '',
    issuedDate: '',
    expiryDate: '',
    documentUrl: '',
    notes: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.issuedBy || !formData.issuedDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      await certificatesApi.create({
        ...formData,
        expiryDate: formData.expiryDate || undefined,
        documentUrl: formData.documentUrl || undefined,
        notes: formData.notes || undefined,
      });
      toast.success('Certificate created');
      navigate('/certificates');
    } catch (error) {
      toast.error('Failed to create certificate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        title="Add Certificate"
        description="Details required to register a certificate against a stock item or batch."
        actions={
          <Button variant="outline" onClick={() => navigate('/certificates')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Compliance
          </Button>
        }
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Certificate Details */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Certificate Details</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="type">Certificate Type</Label>
              <Select
                value={formData.type}
                onValueChange={(v: CertificateType) => setFormData({ ...formData, type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(certTypeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Certificate Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="API 5CT Grade P110 Certification"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="issuedBy">Issued By *</Label>
              <Input
                id="issuedBy"
                value={formData.issuedBy}
                onChange={(e) => setFormData({ ...formData, issuedBy: e.target.value })}
                placeholder="Certifying Authority"
              />
            </div>
          </div>

          {/* Dates */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Dates</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="issuedDate">Issue Date *</Label>
                <Input
                  id="issuedDate"
                  type="date"
                  value={formData.issuedDate}
                  onChange={(e) => setFormData({ ...formData, issuedDate: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expiryDate">Expiry Date</Label>
                <Input
                  id="expiryDate"
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Document */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Document</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="documentUrl">Document URL</Label>
              <Input
                id="documentUrl"
                type="url"
                value={formData.documentUrl}
                onChange={(e) => setFormData({ ...formData, documentUrl: e.target.value })}
                placeholder="https://example.com/certificate.pdf"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/certificates')}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Creating...' : 'Create Certificate'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
